// Nutrition targets — the numbers the Food view compares a day against.
//
// ONE home. Every target this module states lives here; Food.js and
// proteinRunRate.js import it, nothing redeclares it.
// Pure data + pure functions: no React, no network, loadable by the probe
// shim on the same terms as burn.js and burnLabel.js.

// 0.9 g/lb × 200 lb GOAL weight. Anchored to the goal deliberately, not to
// the current weigh-in: protein must stay constant while calories fall, so
// the target cannot be allowed to drift down with the scale.
// (Moved from proteinRunRate.js, which re-exports it.)
export const PROTEIN_TARGET_G_PER_DAY = 180;

// null means "use the day's computed maintenance from burn.js as the target".
// Set to a number (e.g. 1587) for an explicit intake target.
export const CALORIE_TARGET_CAL = null;

// direction names which side of the target is the good side:
//   'ceiling' — lower is better; crossing above it reads 'over'
//   'floor'   — higher is better; sitting below it reads 'under'
//
// PLACEHOLDER VALUES — Marc will tune these. Do not cite them as settled.
export const NUTRIENT_TARGETS = {
  Sodium:     { key: 'Sodium',     label: 'Sodium',    target: 5000, unit: 'mg',  direction: 'ceiling' },
  Potassium:  { key: 'Potassium',  label: 'Potassium', target: 3400, unit: 'mg',  direction: 'floor' },
  Magnesium:  { key: 'Magnesium',  label: 'Magnesium', target: 420,  unit: 'mg',  direction: 'floor' },
  Calcium:    { key: 'Calcium',    label: 'Calcium',   target: 1000, unit: 'mg',  direction: 'floor' },
  Protein:    { key: 'Protein',    label: 'Protein',   target: PROTEIN_TARGET_G_PER_DAY, unit: 'g', direction: 'floor' },
  IodineMcg:  { key: 'IodineMcg',  label: 'Iodine',    target: 1100, unit: 'mcg', direction: 'ceiling' },
  RetinolMcg: { key: 'RetinolMcg', label: 'Retinol',   target: 900,  unit: 'mcg', direction: 'floor' },
  // Not rendered as a tile — exists for a future popover, same as Protein.
  Fat:        { key: 'Fat',        label: 'Fat',       target: 90,   unit: 'g',   direction: 'ceiling' },
};

// { state, ratio } for one nutrient total against one target.
//
//   'unknown'    — value is null: nothing disclosed it. No colour.
//   'incomplete' — undisclosed > 0: the value is a FLOOR, not a measurement.
//                  No colour. This branch is deliberate and non-negotiable —
//                  colouring a floor would assert a deficiency the data
//                  cannot support. Checked BEFORE good/over/under so a floor
//                  can never be judged.
//   'good'       — fully disclosed and on the right side of the target.
//   'over'       — ceiling crossed.
//   'under'      — floor not reached.
//
// ratio is value/target whenever a value exists (a floor still has a floor
// ratio); null when nothing was disclosed. Never coerces null to 0.
export function nutrientStatus({ value, undisclosed, target, direction }) {
  if (value === undefined || value === null) return { state: 'unknown', ratio: null };
  const ratio = target > 0 ? value / target : null;
  if (undisclosed > 0) return { state: 'incomplete', ratio };
  if (direction === 'ceiling') return { state: value > target ? 'over' : 'good', ratio };
  return { state: value < target ? 'under' : 'good', ratio };
}

// State -> colour, resolved in ONE place so the ring and the tiles cannot
// drift. The names resolve to custom properties declared in Food.css on both
// .Food and .Food-popover (the popover portals to document.body, outside
// .Food's scope). null means "inherit the default text colour" — unknown and
// incomplete are judged by no colour at all. (The calorie ring resolves the
// null itself — see ringColor in Food.js for why it cannot inherit.)
const STATUS_COLOR = {
  good: 'var(--food-status-good)',
  over: 'var(--food-status-over)',
  under: 'var(--food-status-under)',
  unknown: null,
  incomplete: null,
};

export function statusColor(state) {
  return STATUS_COLOR[state] ?? null;
}
